import React, { InputHTMLAttributes, useEffect, useRef, useState } from "react";

import {
  formatCNPJ,
  formatCPF,
} from "pages/[tenant]/console/users/utils/userUtils";

import { validaCpfCnpj } from "@Console/users/utils/userUtils";
import { captureException, captureMessage } from "@sentry/nextjs";
import { useField } from "@unform/core";

import { Container, Input, InvalidMessage, Label, Required } from "./styles";

interface IInputStyledProps extends InputHTMLAttributes<HTMLInputElement> {
  name: string;
  label: string;
  inputError: string;
  setInputError: React.Dispatch<React.SetStateAction<string>>;
}

const DocumentInput = ({
  name,
  label,
  inputError,
  setInputError,
  required,
  placeholder,
  ...rest
}: IInputStyledProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const { fieldName, defaultValue, registerField, error, clearError } =
    useField(name);

  const onPressEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.code === "Enter") {
      e.preventDefault();
      e.isPropagationStopped();
    }
  };

  const onChangeDocument = () => {
    const inputValue = inputRef.current?.value || "";
    const isValid = validaCpfCnpj(inputValue);

    if (!isValid) {
      setInputError("Documento inválido");
    } else {
      setInputError("");
      clearError();
    }
  };

  useEffect(() => {
    try {
      if (!defaultValue || !inputRef?.current) return;

      const inputIsCNPJ = defaultValue?.length > 14;
      const formattedValue = inputIsCNPJ
        ? formatCNPJ(defaultValue)
        : formatCPF(defaultValue);

      inputRef.current.value = formattedValue;
    } catch (error) {
      console.log("Erro (DocumentInput | useEffect): ", error);
      captureException(error);
      captureMessage("Erro (DocumentInput | useEffect)");
    }
  }, [defaultValue]);

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: inputRef,
      getValue: (ref) => {
        return ref.current.value;
      },
      setValue: (ref, value) => {
        ref.current.value = value;
      },
      clearValue: (ref) => {
        ref.current.value = "";
      },
    });
  }, [fieldName, registerField]);

  return (
    <Container>
      {label && (
        <Label>
          {label}
          {required && <Required> *</Required>}
        </Label>
      )}

      <Input
        id={fieldName}
        error={!!error}
        inputRef={inputRef}
        onFocus={clearError}
        onKeyPress={onPressEnter}
        defaultValue={defaultValue}
        autoComplete="do-not-autofill"
        placeholder={placeholder || label}
        inputMode="numeric"
        onChange={onChangeDocument}
        {...rest}
      />

      {(error || inputError) && (
        <InvalidMessage>{error || inputError}</InvalidMessage>
      )}
    </Container>
  );
};

export default DocumentInput;
